import {
  agentDetailSchema,
  agentDiffSchema,
  agentRunSchema,
  agentSchema,
  agentVersionResultSchema,
  agentVersionSchema,
  agentVersionSummarySchema,
  channelSchema,
  conversationAgentSchema,
  promptDetailSchema,
  promptDiffSchema,
  promptPublishResultSchema,
  promptSchema,
  promptVersionResultSchema,
  promptVersionSchema,
  type AgentDetail,
  type AgentDiffView,
  type AgentRunView,
  type AgentVersionResult,
  type AgentVersionSummary,
  type AgentVersionView,
  type AgentView,
  type ChannelView,
  type ConversationAgentView,
  type CreateAgentInput,
  type CreateAgentVersionInput,
  type CreatePromptInput,
  type CreatePromptVersionInput,
  type PromptDetail,
  type PromptDiffView,
  type PromptPublishResult,
  type PromptVersionResult,
  type PromptVersionView,
  type PromptView,
  type UpdateAgentInput,
  type UpdatePromptInput,
} from '@ai-sales/shared'
import { z } from 'zod'
import { apiRequest } from '../lib/api-client.js'

/**
 * Промпты и агенты.
 *
 * Схема ответа передаётся в каждый вызов и проверяется на клиенте. Это не
 * недоверие к своему же серверу: схема одна и та же для обеих сторон, и
 * проверка ловит рассинхрон версий — открытую вкладку со старым кодом
 * после выката. Без неё такая вкладка падала бы где-то в отрисовке,
 * с сообщением, по которому причину не найти.
 */

const okSchema = z.object({ ok: z.literal(true) })

// ─── промпты ───

export async function fetchPrompts(workspaceId: string): Promise<PromptView[]> {
  return apiRequest(`/workspaces/${workspaceId}/prompts`, z.array(promptSchema))
}

export async function fetchPrompt(workspaceId: string, promptId: string): Promise<PromptDetail> {
  return apiRequest(`/workspaces/${workspaceId}/prompts/${promptId}`, promptDetailSchema)
}

export async function fetchPromptVersion(
  workspaceId: string,
  promptId: string,
  versionId: string,
): Promise<PromptVersionView> {
  return apiRequest(
    `/workspaces/${workspaceId}/prompts/${promptId}/versions/${versionId}`,
    promptVersionSchema,
  )
}

export async function fetchPromptDiff(
  workspaceId: string,
  promptId: string,
  from: string,
  to: string,
): Promise<PromptDiffView> {
  return apiRequest(`/workspaces/${workspaceId}/prompts/${promptId}/diff`, promptDiffSchema, {
    query: { from, to },
  })
}

export async function createPrompt(
  workspaceId: string,
  input: CreatePromptInput,
): Promise<PromptDetail> {
  return apiRequest(`/workspaces/${workspaceId}/prompts`, promptDetailSchema, {
    method: 'POST',
    body: input,
  })
}

export async function updatePrompt(
  workspaceId: string,
  promptId: string,
  input: UpdatePromptInput,
): Promise<PromptDetail> {
  return apiRequest(`/workspaces/${workspaceId}/prompts/${promptId}`, promptDetailSchema, {
    method: 'PATCH',
    body: input,
  })
}

export async function createPromptVersion(
  workspaceId: string,
  promptId: string,
  input: CreatePromptVersionInput,
): Promise<PromptVersionResult> {
  return apiRequest(
    `/workspaces/${workspaceId}/prompts/${promptId}/versions`,
    promptVersionResultSchema,
    { method: 'POST', body: input },
  )
}

/** Публикация версии — она же откат. Разного действия на сервере нет. */
export async function publishPromptVersion(
  workspaceId: string,
  promptId: string,
  versionId: string,
): Promise<PromptPublishResult> {
  return apiRequest(
    `/workspaces/${workspaceId}/prompts/${promptId}/publish`,
    promptPublishResultSchema,
    { method: 'POST', body: { versionId } },
  )
}

export async function deletePrompt(workspaceId: string, promptId: string): Promise<void> {
  await apiRequest(`/workspaces/${workspaceId}/prompts/${promptId}`, okSchema, {
    method: 'DELETE',
  })
}

// ─── агенты ───

export async function fetchAgents(workspaceId: string): Promise<AgentView[]> {
  return apiRequest(`/workspaces/${workspaceId}/agents`, z.array(agentSchema))
}

export async function fetchAgent(workspaceId: string, agentId: string): Promise<AgentDetail> {
  return apiRequest(`/workspaces/${workspaceId}/agents/${agentId}`, agentDetailSchema)
}

export async function fetchAgentVersion(
  workspaceId: string,
  agentId: string,
  versionId: string,
): Promise<AgentVersionView> {
  return apiRequest(
    `/workspaces/${workspaceId}/agents/${agentId}/versions/${versionId}`,
    agentVersionSchema,
  )
}

export async function fetchAgentDiff(
  workspaceId: string,
  agentId: string,
  from: string,
  to: string,
): Promise<AgentDiffView> {
  return apiRequest(`/workspaces/${workspaceId}/agents/${agentId}/diff`, agentDiffSchema, {
    query: { from, to },
  })
}

export async function createAgent(
  workspaceId: string,
  input: CreateAgentInput,
): Promise<AgentDetail> {
  return apiRequest(`/workspaces/${workspaceId}/agents`, agentDetailSchema, {
    method: 'POST',
    body: input,
  })
}

export async function updateAgent(
  workspaceId: string,
  agentId: string,
  input: UpdateAgentInput,
): Promise<AgentDetail> {
  return apiRequest(`/workspaces/${workspaceId}/agents/${agentId}`, agentDetailSchema, {
    method: 'PATCH',
    body: input,
  })
}

export async function createAgentVersion(
  workspaceId: string,
  agentId: string,
  input: CreateAgentVersionInput,
): Promise<AgentVersionResult> {
  return apiRequest(
    `/workspaces/${workspaceId}/agents/${agentId}/versions`,
    agentVersionResultSchema,
    { method: 'POST', body: input },
  )
}

export async function publishAgentVersion(
  workspaceId: string,
  agentId: string,
  versionId: string,
): Promise<AgentVersionSummary> {
  return apiRequest(
    `/workspaces/${workspaceId}/agents/${agentId}/publish`,
    agentVersionSummarySchema,
    { method: 'POST', body: { versionId } },
  )
}

export async function deleteAgent(workspaceId: string, agentId: string): Promise<void> {
  await apiRequest(`/workspaces/${workspaceId}/agents/${agentId}`, okSchema, { method: 'DELETE' })
}

// ─── каналы и диалоги ───

export async function fetchChannels(workspaceId: string): Promise<ChannelView[]> {
  return apiRequest(`/workspaces/${workspaceId}/channels`, z.array(channelSchema))
}

export async function bindChannelAgent(
  workspaceId: string,
  channelId: string,
  agentId: string | null,
): Promise<ChannelView> {
  return apiRequest(`/workspaces/${workspaceId}/channels/${channelId}`, channelSchema, {
    method: 'PATCH',
    body: { agentId },
  })
}

export async function fetchConversationAgent(
  workspaceId: string,
  conversationId: string,
): Promise<ConversationAgentView> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}/agent`,
    conversationAgentSchema,
  )
}

/**
 * Прогоны агента по диалогу — от нового к старому.
 *
 * Отдельно от карточки диалога намеренно: переписка нужна всегда, шаги —
 * когда разбираются, и грузить их вместе значило бы платить за них
 * на каждом открытии экрана.
 */
export async function fetchConversationRuns(
  workspaceId: string,
  conversationId: string,
): Promise<AgentRunView[]> {
  return apiRequest(
    `/workspaces/${workspaceId}/conversations/${conversationId}/runs`,
    z.array(agentRunSchema),
  )
}
